import { actions } from "@grandvisionhq/state";
import { isNewPrescriptionAndUpload } from "../../../../utils/utils/is-new-prescription-upload.js";
import { startPrescriptionGlassesConfiguration } from "../../../state/actions.js";
import { NEW_PRESCRIPTION, RX_SCOPE } from "../../../state/constants.js";
import { selectLensTypeSuccess, selectPackageSuccess, resetSelectedPackage, selectAddOnSuccess, resetSelectedAddOn, updatePrescriptionValue, updatePrescriptionRecognitionValues, updatePrescriptionInfo, updatePrescriptionType, updateVerificationAllowed, updateUploadReference, removeUploadReference } from "./actions.js";
const getPrescriptionType = (s, frameSku) => s[frameSku]?.prescriptionType || NEW_PRESCRIPTION;
const reducer = actions.reducer(
  {},
  /**
   * Create a blank configuration but only on the first call
   */
  actions.on(
    startPrescriptionGlassesConfiguration,
    (s, { payload: { frameSku, preselectedPrescriptionEntry } }) => {
      if (frameSku in s) {
        return s;
      }
      return {
        ...s,
        [frameSku]: {
          prescriptionType: preselectedPrescriptionEntry || NEW_PRESCRIPTION
        }
      };
    }
  ),
  /**
   * Changes the selected lens type. If no prescriptionPrefill is passed along we reset the prescription data
   */
  actions.on(selectLensTypeSuccess, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (currentLensType?.sku === payload.lensTypeSku) {
      return s;
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          sku: payload.lensTypeSku,
          prescriptionInfo: {
            ...currentLensType?.prescriptionInfo ?? {
              uploadReferences: {}
            },
            ...payload.prescriptionPrefill ?? {
              left: {},
              right: {}
            }
          }
        }
      }
    };
  }),
  /**
   * Changes the selected package for the given frameSku
   */
  actions.on(
    selectPackageSuccess,
    (s, { payload: { frameSku, packageSku } }) => {
      const frameConfig = s[frameSku];
      if (frameConfig?.package === packageSku) {
        return s;
      }
      return {
        ...s,
        [frameSku]: {
          ...s[frameSku],
          prescriptionType: getPrescriptionType(s, frameSku),
          package: packageSku
        }
      };
    }
  ),
  /**
   * This is used for when there's a package selected that isn't compatible with the current lens-type
   */
  actions.on(
    resetSelectedPackage,
    (s, { payload: { frameSku } }) => ({
      ...s,
      [frameSku]: {
        ...s[frameSku],
        prescriptionType: getPrescriptionType(s, frameSku),
        package: void 0,
        addOn: void 0
      }
    })
  ),
  /**
   * Changes the selected add-on for the given frameSku
   */
  actions.on(selectAddOnSuccess, (s, { payload: { frameSku, addOnSku } }) => {
    const frameConfig = s[frameSku];
    if (frameConfig?.addOn === addOnSku) {
      return s;
    }
    return {
      ...s,
      [frameSku]: {
        ...s[frameSku],
        prescriptionType: getPrescriptionType(s, frameSku),
        addOn: addOnSku
      }
    };
  }),
  /**
   * This is used for when there's a add-on selected that isn't compatible with the current package
   */
  actions.on(
    resetSelectedAddOn,
    (s, { payload: { frameSku } }) => ({
      ...s,
      [frameSku]: {
        ...s[frameSku],
        prescriptionType: getPrescriptionType(s, frameSku),
        addOn: void 0
      }
    })
  ),
  /**
   * Changes the prescription parameters' value for the given frameSku
   */
  actions.on(updatePrescriptionValue, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    const currentSide = currentLensType.prescriptionInfo[payload.side];
    if (currentSide[payload.parameter] === payload.value) {
      return s;
    }
    let updatedSide = {
      ...currentSide,
      [payload.parameter]: payload.value
    };
    if (payload.value === void 0) {
      updatedSide = Object.fromEntries(
        Object.entries(currentSide).filter(([key]) => key !== payload.parameter)
      );
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            [payload.side]: updatedSide
          }
        }
      }
    };
  }),
  /**
   * set all the prescription values received by prescription recognition
   */
  actions.on(updatePrescriptionRecognitionValues, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            left: {
              ...payload.left
            },
            right: {
              ...payload.right
            }
          }
        }
      }
    };
  }),
  /**
   * Changes the prescription info for the given frameSku
   */
  actions.on(updatePrescriptionInfo, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: s[payload.frameSku]?.prescriptionType || NEW_PRESCRIPTION,
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            additionalInfo: payload.value
          }
        }
      }
    };
  }),
  /**
   * Changes the type of prescription, i.e. existing, email upload, manual input, etc...
   */
  actions.on(updatePrescriptionType, (s, { payload }) => {
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: payload.prescriptionType
      }
    };
  }),
  /**
   * Changes the verification allowed info for the given frameSku
   */
  actions.on(updateVerificationAllowed, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            verificationAllowed: payload.value
          }
        }
      }
    };
  }),
  actions.on(updateUploadReference, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    const { left, right } = currentLensType.prescriptionInfo;
    const rxValues = { left, right };
    const prescriptionType = getPrescriptionType(s, payload.frameSku);
    if (payload.scope === RX_SCOPE && !isNewPrescriptionAndUpload(prescriptionType)) {
      rxValues.left = {};
      rxValues.right = {};
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            ...rxValues,
            uploadReferences: {
              ...currentLensType.prescriptionInfo.uploadReferences,
              [payload.scope]: payload.uploadReference
            }
          }
        }
      }
    };
  }),
  actions.on(removeUploadReference, (s, { payload }) => {
    const currentLensType = s[payload.frameSku]?.lensType;
    if (!currentLensType) {
      return s;
    }
    const {
      uploadReferences: currentUploadReferences,
      left,
      right
    } = currentLensType.prescriptionInfo;
    const { [payload.scope]: _toBeRemoved, ...remainingScopes } = currentUploadReferences || {};
    const rxValues = { left, right };
    if (payload.scope === RX_SCOPE && payload.removeRxValues) {
      rxValues.left = {};
      rxValues.right = {};
    }
    return {
      ...s,
      [payload.frameSku]: {
        ...s[payload.frameSku],
        prescriptionType: getPrescriptionType(s, payload.frameSku),
        lensType: {
          ...currentLensType,
          prescriptionInfo: {
            ...currentLensType.prescriptionInfo,
            ...rxValues,
            uploadReferences: remainingScopes
          }
        }
      }
    };
  })
);
export {
  reducer
};
//# sourceMappingURL=reducer.js.map
